import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { appServicesAPI, clustersAPI } from '../../services/api';
import ImageSelector from '../../components/ImageSelector';

const SIZES = [
  { id:'xs',     label:'XSmall', cpu_req:'0.5', cpu_lim:'1',  mem_req:0.5, mem_lim:1,  desc:'0.5 vCPU · 512MB' },
  { id:'small',  label:'Small',  cpu_req:'1',   cpu_lim:'2',  mem_req:1,   mem_lim:2,  desc:'1 vCPU · 1GB' },
  { id:'medium', label:'Medium', cpu_req:'2',   cpu_lim:'4',  mem_req:2,   mem_lim:4,  desc:'2 vCPU · 2GB' },
  { id:'large',  label:'Large',  cpu_req:'4',   cpu_lim:'8',  mem_req:4,   mem_lim:8,  desc:'4 vCPU · 4GB' },
  { id:'custom', label:'Custom', cpu_req:'',    cpu_lim:'',   mem_req:'',  mem_lim:'', desc:'Set manually' },
];

const isBatchKind = (kind) => kind === 'Job' || kind === 'CronJob';

const SERVICE_TEMPLATES = [
  { label:'mob-i4cbackend',   name:'mob-i4cbackend',     image:'',                                                     kind:'Deployment',  size:'large',  port:8080, env_vars:'{}' },
  { label:'Elasticsearch',    name:'elasticsearch-es01',  image:'docker.elastic.co/elasticsearch/elasticsearch:8.13.0', kind:'StatefulSet', size:'medium', port:9200, env_vars:'{"discovery.type":"single-node","ES_JAVA_OPTS":"-Xms2g -Xmx2g","xpack.security.enabled":"false"}' , single:true, cluster_in_catalog:'Elasticsearch (SarvKube) → Multi-node cluster' },
  { label:'Kibana',           name:'kibana',              image:'docker.elastic.co/kibana/kibana:8.13.0',                kind:'Deployment',  size:'small',  port:5601, env_vars:'{"ELASTICSEARCH_HOSTS":"http://elasticsearch-es01:9200"}' },
  { label:'Redis',            name:'redis-master',        image:'redis:7.2-alpine',                                     kind:'StatefulSet', size:'xs',     port:6379, env_vars:'{}' , single:true, cluster_in_catalog:'Redis (SarvKube) → Sentinel + TLS' },
  { label:'RabbitMQ',         name:'rabbitmq-0',          image:'rabbitmq:3.13-management-alpine',                      kind:'StatefulSet', size:'small',  port:5672, env_vars:'{}' , single:true },
  { label:'MinIO',            name:'minio',               image:'minio/minio:RELEASE.2024-01-16T16-07-38Z',             kind:'StatefulSet', size:'medium', port:9000, env_vars:'{"MINIO_ROOT_USER":"admin","MINIO_ROOT_PASSWORD":"minio@admin123"}' , single:true },
  { label:'PostgreSQL',       name:'postgres',            image:'postgres:15-alpine',                                   kind:'StatefulSet', size:'small',  port:5432, env_vars:'{"POSTGRES_USER":"app","POSTGRES_PASSWORD":"changeme","POSTGRES_DB":"appdb"}' , single:true, cluster_in_catalog:'PostgreSQL (SarvKube) → Patroni HA' },
  { label:'MySQL',            name:'mysql',               image:'mysql:8.0',                                            kind:'StatefulSet', size:'small',  port:3306, env_vars:'{"MYSQL_ROOT_PASSWORD":"changeme","MYSQL_DATABASE":"appdb"}' , single:true },
];

export default function AppServiceCreatePage() {
  const navigate = useNavigate();
  const fileRef  = useRef();
  const [mode, setMode]   = useState('form'); // form | yaml
  const [clusters, setClusters]   = useState([]);
  const [configRes, setConfigRes] = useState({ configmaps:[], secrets:[] });
  const [form, setForm] = useState({
    cluster_id:'', name:'', namespace:'i4c-apps', kind:'Deployment', replicas:1,
    image:'', size:'small',
    cpu_request:'1', cpu_limit:'2', mem_request_gb:1, mem_limit_gb:2,
    storage_gb:'', storage_class:'',
    existing_pvc:'', existing_pvc_mount:'/mnt/data',   // attach an already-created PVC
    env_vars:'{}',
    labels:[],   // [{key, value}] — custom labels merged onto app/managed-by server-side
    configmap_refs:[], secret_refs:[],
    // Mount an existing ConfigMap/Secret/PVC as a FILE (not envFrom — see configmap_refs/
    // secret_refs above for that). [{type: configMap|secret|persistentVolumeClaim, name_ref, mount_path}]
    volumes:[],
    // Scheduling
    node_selector_key:'', node_selector_val:'',
    image_pull_policy:'Always',
    image_pull_secret:'',
    toleration_key:'', toleration_effect:'NoSchedule',
    // Job/CronJob only — restartPolicy must never be "Always" for batch/v1 (K8s rejects
    // the whole object), so this is a separate, narrower choice from a normal pod's.
    restart_policy:'OnFailure',
    backoff_limit:'', active_deadline_seconds:'', completions:'', parallelism:'',
    schedule:'', concurrency_policy:'Allow', suspend:false,
    successful_jobs_history_limit:'', failed_jobs_history_limit:'',
  });
  const [showScheduling, setShowScheduling] = useState(false);
  const [yamlContent, setYamlContent] = useState('');
  const [deploying, setDeploying]   = useState(false);
  // Post-deploy summary — My Workloads has no App-Catalog-style connection-info screen,
  // so whatever password the user typed (or the generate button filled in) was otherwise
  // never shown back to them again. Surfaces exactly what's already known client-side:
  // no new backend call, just the values this form already has.
  const [deployResult, setDeployResult] = useState(null);
  const [error, setError]           = useState('');
  const [nodes, setNodes]           = useState([]);      // live worker capacity
  const [nodesLoading, setNodesLoading] = useState(false);
  const [pvcs, setPvcs]             = useState([]);      // for the Volumes section's PVC picker

  const load = useCallback(async () => {
    try {
      const [cR] = await Promise.all([clustersAPI.list()]);
      const cl = cR.data || [];
      setClusters(cl);
      if (cl.length) setForm(f => ({...f, cluster_id: cl[0].id}));
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load configmaps/secrets/PVCs when cluster OR namespace changes — all three are
  // namespace-scoped, so a cluster-wide-only fetch would offer objects from namespaces
  // the user isn't even deploying into.
  //
  // Uses the Steve-backed /paged endpoints (same ones Secrets.js/ConfigMaps.js/PVCs.js
  // already use), NOT clustersAPI.getConfigResources — found live (latency audit) that
  // even namespace-scoped, getConfigResources still takes ~12s on a Rancher cluster,
  // because it always goes through the raw Kubernetes SDK + a VPN-wrapper connection
  // setup (_run_with_vpn in _legacy.py) regardless of whether Steve is available. The
  // /paged endpoints route through Steve's already-open Rancher session instead —
  // confirmed live at ~0.1-0.3s for the identical data.
  useEffect(() => {
    if (!form.cluster_id) return;
    const pagedItems = (resource) => clustersAPI.getWorkloadPaged(resource, form.cluster_id, { pageSize: 500, namespace: form.namespace })
      .then(r => (r.data?.items || []).map(it => ({ name: it.name, namespace: it.namespace })))
      .catch(() => []);
    pagedItems('configmaps').then(configmaps => setConfigRes(c => ({ ...c, configmaps })));
    pagedItems('secrets').then(secrets => setConfigRes(c => ({ ...c, secrets })));
    clustersAPI.getWorkloadPaged('persistentvolumeclaims', form.cluster_id, { pageSize: 500, namespace: form.namespace })
      .then(r => {
        const d = r.data || {};
        const raw = d.pvcs || d.items || [];
        setPvcs(raw.map(it => it.metadata?.name || it.name).filter(Boolean));
      })
      .catch(() => setPvcs([]));
  }, [form.cluster_id, form.namespace]);

  // Load live node capacity (CPU/mem available) when cluster changes — for the node picker.
  useEffect(() => {
    if (!form.cluster_id) { setNodes([]); return; }
    setNodesLoading(true);
    clustersAPI.nodeCapacity(form.cluster_id)
      .then(r => {
        const ns = (r.data?.nodes) || [];
        setNodes(ns);
        if (ns.length) setShowScheduling(true); // surface the node picker when nodes are available
      })
      .catch(() => setNodes([]))
      .finally(() => setNodesLoading(false));
  }, [form.cluster_id]);

  const applySize = (id) => {
    const s = SIZES.find(x=>x.id===id);
    if (!s || id==='custom') { setF('size',id); return; }
    setForm(f=>({...f, size:id, cpu_request:s.cpu_req, cpu_limit:s.cpu_lim,
      mem_request_gb:s.mem_req, mem_limit_gb:s.mem_lim}));
  };

  // A database template here is ONE node: its settings (e.g. Elasticsearch's
  // discovery.type=single-node) make extra replicas independent copies, not a cluster —
  // 3 replicas used to yield 3 separate one-node clusters. Lock replicas to 1 and point to
  // the App Catalog, which builds real clusters.
  const [singleTpl, setSingleTpl] = useState(null);

  const applyTemplate = (tpl) => {
    const sz = SIZES.find(x=>x.id===tpl.size) || SIZES[1];
    setSingleTpl(tpl.single ? tpl : null);
    setForm(f=>({...f, name:tpl.name, image:tpl.image, kind:tpl.kind, ...(tpl.single ? { replicas: 1 } : {}),
      env_vars:tpl.env_vars,
      size:tpl.size, cpu_request:sz.cpu_req, cpu_limit:sz.cpu_lim,
      mem_request_gb:sz.mem_req, mem_limit_gb:sz.mem_lim}));
    setMode('form');
  };

  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  // Env Vars has no per-engine knowledge of "which key is a password" (unlike App
  // Catalog's _pw() pattern, which knows e.g. POSTGRES_PASSWORD specifically) — this form
  // is generic for any image. So the user marks a field as "please generate this" by
  // leaving its value blank, and this fills every blank-string value with a strong
  // random one. Does nothing to keys that already have a value.
  const generateRandomValue = () => {
    const bytes = new Uint8Array(18);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    return btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, '').slice(0, 20);
  };

  const generateBlankEnvValues = () => {
    let parsed;
    try { parsed = JSON.parse(form.env_vars || '{}'); }
    catch { setError('Environment Variables: invalid JSON — fix the syntax first'); return; }
    if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
      setError('Environment Variables must be a JSON object'); return;
    }
    let changed = 0;
    const next = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (v === '') { next[k] = generateRandomValue(); changed += 1; }
      else next[k] = v;
    }
    if (!changed) { setError('No blank-value fields found to generate — set a value to "" to mark it for generation.'); return; }
    setError('');
    setF('env_vars', JSON.stringify(next, null, 2));
  };

  const toggleRef = (field, val) => setForm(f => ({
    ...f,
    [field]: f[field].includes(val) ? f[field].filter(x=>x!==val) : [...f[field], val],
  }));

  const addVolume = () => setForm(f => ({ ...f, volumes: [...f.volumes, { type:'configMap', name_ref:'', mount_path:'' }] }));
  const setVolume = (i, field, val) => setForm(f => {
    const next = [...f.volumes];
    next[i] = { ...next[i], [field]: val };
    if (field === 'type') next[i].name_ref = '';  // options differ per type, stale pick would be wrong
    return { ...f, volumes: next };
  });
  const removeVolume = (i) => setForm(f => ({ ...f, volumes: f.volumes.filter((_, j) => j !== i) }));

  const addLabel    = () => setForm(f => ({ ...f, labels: [...f.labels, { key:'', value:'' }] }));
  const setLabel     = (i, k, v) => setForm(f => {
    const next = [...f.labels]; next[i] = { ...next[i], [k]: v }; return { ...f, labels: next };
  });
  const removeLabel = (i) => setForm(f => ({ ...f, labels: f.labels.filter((_,j)=>j!==i) }));

  // Parse YAML to extract metadata. Multi-document aware: a paste with several
  // `---`-separated objects (e.g. Service + PVC + StatefulSet) used to run ONE regex
  // over the WHOLE blob, so it picked up the FIRST kind/name/image found anywhere —
  // for Service+StatefulSet that meant kind="Service" shown next to the StatefulSet's
  // image, a confusing/wrong summary even though the actual kubectl apply (server-side,
  // doc-aware already) deploys every object correctly. Split into documents first, then
  // prefer whichever one is the real WORKLOAD (the thing with an image) for display.
  const WORKLOAD_KIND_PREFERENCE = ['Deployment', 'StatefulSet', 'DaemonSet', 'Job', 'CronJob', 'Pod'];
  const parseYamlMeta = (yaml) => {
    const docs = yaml.split(/^---\s*$/m).map(d => d.trim()).filter(Boolean);
    const parsedDocs = docs.map(doc => {
      const kindM  = doc.match(/^kind:\s*(\S+)/m);
      const nameM  = doc.match(/^metadata:[\s\S]*?^\s+name:\s*(\S+)/m);
      const nsM    = doc.match(/^metadata:[\s\S]*?^\s+namespace:\s*(\S+)/m);
      const imageM = doc.match(/^\s+image:\s*(\S+)/m);
      return {
        kind:  kindM?.[1] || null,
        name:  nameM?.[1] || null,
        ns:    nsM?.[1]   || null,
        image: imageM?.[1]|| null,
      };
    });
    let primary = null;
    for (const pref of WORKLOAD_KIND_PREFERENCE) {
      primary = parsedDocs.find(d => d.kind === pref);
      if (primary) break;
    }
    // No recognized workload kind (e.g. only a Service/ConfigMap pasted) — fall back to
    // whichever document actually has a name, same as the single-doc behavior before.
    if (!primary) primary = parsedDocs.find(d => d.name) || parsedDocs[0] || {};
    return primary;
  };

  const handleDeploy = async () => {
    if (!form.cluster_id) { setError('Select a cluster'); return; }
    setDeploying(true); setError('');
    try {
      if (mode === 'yaml') {
        if (!yamlContent.trim()) { setError('YAML content is empty'); setDeploying(false); return; }
        const meta = parseYamlMeta(yamlContent);
        await appServicesAPI.create({
          cluster_id: parseInt(form.cluster_id),
          name: meta.name || `yaml-${Date.now()}`,
          display_name: meta.name || 'YAML Deploy',
          image: meta.image || 'n/a',
          kind:  meta.kind || 'raw',
          namespace: meta.ns || form.namespace || 'i4c-apps',
          raw_yaml: yamlContent,
          replicas:1, cpu_request:'1', cpu_limit:'2', mem_request_gb:1, mem_limit_gb:2,
        });
      } else {
        if (!form.name.trim()) { setError('Service name is required'); setDeploying(false); return; }
        if (!form.image.trim()) { setError('Container image is required'); setDeploying(false); return; }
        if (form.kind === 'CronJob' && !form.schedule.trim()) {
          setError('Schedule is required for a CronJob'); setDeploying(false); return;
        }
        let env_vars={};
        try { env_vars = JSON.parse(form.env_vars||'{}'); } catch { setError('Env vars: invalid JSON'); setDeploying(false); return; }

        // Build scheduling / advanced notes
        const nodeSelector = form.node_selector_key
          ? {[form.node_selector_key]: form.node_selector_val} : {};
        const tolerations = form.toleration_key
          ? [{key: form.toleration_key, operator:'Exists', effect: form.toleration_effect||'NoSchedule'}] : [];
        // Attach an EXISTING PVC (the original single-field Storage-section shortcut) as
        // an extra volume+mount (backend renders extra_volumes[type=pvc] →
        // persistentVolumeClaim, extra_mounts → mount).
        const existingPvc = (form.existing_pvc || '').trim();
        const extraVols = existingPvc
          ? [{ name: 'existing-pvc', type: 'pvc', source: existingPvc }] : [];
        const extraMounts = existingPvc
          ? [{ name: 'existing-pvc', mountPath: (form.existing_pvc_mount || '/mnt/data').trim() }] : [];
        // The general-purpose Volumes section — ConfigMap/Secret/PVC mounted as a FILE
        // (distinct from configmap_refs/secret_refs' envFrom above). backend's extra_volumes
        // "type" values are lowercase-first (configMap/secret/pvc), not the k8s API's own
        // casing, so persistentVolumeClaim maps to "pvc" here.
        const BACKEND_VOL_TYPE = { configMap: 'configMap', secret: 'secret', persistentVolumeClaim: 'pvc' };
        form.volumes.filter(v => v.name_ref.trim() && v.mount_path.trim()).forEach((v, i) => {
          const volName = `vol-${i}`;
          extraVols.push({ name: volName, type: BACKEND_VOL_TYPE[v.type], source: v.name_ref.trim() });
          extraMounts.push({ name: volName, mountPath: v.mount_path.trim() });
        });
        const customLabels = Object.fromEntries(
          form.labels.filter(l => l.key.trim()).map(l => [l.key.trim(), l.value])
        );
        // Job/CronJob-only fields — backend reads these from deploy_config (notes).
        const batchFields = isBatchKind(form.kind) ? {
          restart_policy: form.restart_policy,
          backoff_limit: form.backoff_limit === '' ? null : parseInt(form.backoff_limit),
          active_deadline_seconds: form.active_deadline_seconds === '' ? null : parseInt(form.active_deadline_seconds),
          completions: form.completions === '' ? null : parseInt(form.completions),
          parallelism: form.parallelism === '' ? null : parseInt(form.parallelism),
          ...(form.kind === 'CronJob' ? {
            schedule: form.schedule,
            concurrency_policy: form.concurrency_policy,
            suspend: !!form.suspend,
            successful_jobs_history_limit: form.successful_jobs_history_limit === '' ? null : parseInt(form.successful_jobs_history_limit),
            failed_jobs_history_limit: form.failed_jobs_history_limit === '' ? null : parseInt(form.failed_jobs_history_limit),
          } : {}),
        } : {};
        const notes = JSON.stringify({
          annotations:{}, labels:customLabels, raw_yaml:'',
          configmap_refs: form.configmap_refs,
          secret_refs:    form.secret_refs,
          image_pull_policy: form.image_pull_policy || 'Always',
          ...(form.image_pull_secret ? {image_pull_secrets:[form.image_pull_secret]} : {}),
          ...(Object.keys(nodeSelector).length ? {node_selector: nodeSelector} : {}),
          ...(tolerations.length ? {tolerations} : {}),
          ...(extraVols.length ? {extra_volumes: extraVols, extra_mounts: extraMounts} : {}),
          ...batchFields,
        });
        const finalName = form.name.toLowerCase().replace(/[^a-z0-9-]/g,'-');
        const finalNs   = form.namespace || 'i4c-apps';
        await appServicesAPI.create({
          cluster_id:     parseInt(form.cluster_id),
          name:           finalName,
          display_name:   form.name,
          image:          form.image,
          kind:           form.kind,
          namespace:      finalNs,
          replicas:       parseInt(form.replicas)||1,
          cpu_request:    String(form.cpu_request),
          cpu_limit:      String(form.cpu_limit),
          mem_request_gb: parseFloat(form.mem_request_gb)||1,
          mem_limit_gb:   parseFloat(form.mem_limit_gb)||2,
          storage_gb:     parseInt(form.storage_gb)||null,
          storage_class:  form.storage_class||null,
          port:           null,
          service_type:   null,
          subdomain:      null,
          env_vars, annotations:{}, labels:customLabels, notes,
        });
        setDeployResult({
          name: finalName, namespace: finalNs, kind: form.kind,
          clusterName: clusters.find(c => String(c.id) === String(form.cluster_id))?.name || '',
          dnsName: `${finalName}.${finalNs}.svc.cluster.local`,
          env_vars,
        });
        return; // stay on this page to show the summary instead of navigating away
      }
      navigate('/compute/workloads');
    } catch (e) {
      const d = e.response?.data?.detail;
      // 409 name-exists: detail is a structured object ({message, existing_id, name,
      // namespace, code} — app_services.py), not a string. Rendering it directly as
      // {error} crashed with React error #31 — same bug class just fixed on Pods.js/
      // ClusterContext.js for a different endpoint's object-shaped detail. Same safe-unwrap
      // pattern InstanceLaunchPage.js already uses correctly for this exact 409 case.
      if (e.response?.status === 409 && d && typeof d === 'object' && d.code === 'name_exists') {
        setError(`${d.message} Choose a different name, or open the existing one from My Workloads.`);
      } else {
        setError((typeof d === 'string' && d) || 'Deploy failed');
      }
    } finally { setDeploying(false); }
  };

  const lbl = (text, hint) => (
    <label style={{ fontSize:12, fontWeight:600, color:'var(--ink-2)', display:'block', marginBottom:5 }}>
      {text}{hint&&<span style={{ fontWeight:400, color:'var(--muted-2)', marginLeft:6 }}>{hint}</span>}
    </label>
  );
  const section = (title, children, optional=false) => (
    <section className="card">
      <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:18,
        paddingBottom:10, borderBottom:'1px solid var(--surface-2)', display:'flex', alignItems:'center', gap:8 }}>
        {title}
        {optional&&<span style={{ fontSize:11, fontWeight:400, color:'var(--muted-2)' }}>optional</span>}
      </div>
      {children}
    </section>
  );

  if (deployResult) {
    const r = deployResult;
    const envEntries = Object.entries(r.env_vars || {});
    return (
      <div style={{ padding:'24px 32px', maxWidth:680 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:24 }}>
          <h1 style={{ margin:0, fontSize:20, fontWeight:700, color:'var(--ink)' }}>✅ Deployed</h1>
        </div>
        {/* My Workloads has no App-Catalog-style connection-info screen — this is
            everything already known client-side about what was just created, so
            whatever password was typed/generated isn't lost the moment this page
            is left. */}
        <div className="card" style={{ marginBottom:20 }}>
          <div style={{ fontSize:13, fontWeight:700, marginBottom:14 }}>{r.name}</div>
          <div style={{ display:'grid', gridTemplateColumns:'140px 1fr', rowGap:10, fontSize:13 }}>
            <div style={{ color:'var(--muted-2)' }}>Cluster</div><div>{r.clusterName}</div>
            <div style={{ color:'var(--muted-2)' }}>Namespace</div><div><code>{r.namespace}</code></div>
            <div style={{ color:'var(--muted-2)' }}>Kind</div><div>{r.kind}</div>
            {!isBatchKind(r.kind) && (
              <>
                <div style={{ color:'var(--muted-2)' }}>Internal DNS</div>
                <div><code>{r.dnsName}</code></div>
              </>
            )}
          </div>
        </div>
        {envEntries.length > 0 && (
          <div className="card" style={{ marginBottom:20 }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:4 }}>Environment variables set</div>
            <div style={{ fontSize:11, color:'var(--muted-2)', marginBottom:12 }}>
              Copy anything here now — it won't be shown again after you leave this page.
            </div>
            <div style={{ border:'1px solid var(--border)', borderRadius:6, overflow:'hidden' }}>
              {envEntries.map(([k, v], i) => (
                <div key={k} style={{ display:'flex', justifyContent:'space-between', gap:10,
                  padding:'7px 10px', fontFamily:'monospace', fontSize:12,
                  borderTop: i===0 ? 'none' : '1px solid var(--surface-2)' }}>
                  <span>{k}</span>
                  <span style={{ color:'var(--ink-2)', wordBreak:'break-all', textAlign:'right' }}>{String(v)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ display:'flex', gap:12 }}>
          <button onClick={()=>navigate('/compute/workloads')} className="btn btn-primary btn-lg">
            Go to Workloads
          </button>
          <button onClick={()=>setDeployResult(null)} className="btn btn-secondary btn-lg">
            Deploy another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding:'24px 32px', maxWidth:820 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:24 }}>
        <button onClick={()=>navigate('/compute/workloads')} className="btn btn-link">
          ← Workloads
        </button>
        <span style={{ color:'var(--muted-2)' }}>›</span>
        <h1 style={{ margin:0, fontSize:20, fontWeight:700, color:'var(--ink)' }}>Deploy Workload</h1>
      </div>

      {/* Mode toggle */}
      <div className="filter-tabs" style={{ marginBottom:24, width:'fit-content' }}>
        {[['form','📝 Form'],['yaml','📄 YAML']].map(([id,lbl2])=>(
          <button key={id} onClick={()=>setMode(id)} className={mode===id ? 'active' : ''}>
            {lbl2}
          </button>
        ))}
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

        {/* Cluster (always shown) */}
        {section('Cluster & Namespace',
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
            <div>
              {lbl('Target Cluster','*')}
              <select value={form.cluster_id} onChange={e=>setF('cluster_id',e.target.value)} className="select">
                <option value="">— Select —</option>
                {clusters.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              {lbl('Namespace','blank = i4c-apps')}
              <input placeholder="i4c-apps" value={form.namespace}
                onChange={e=>setF('namespace',e.target.value)} className="input" />
            </div>
            {mode==='form' && (
              <div>
                {lbl('Kind')}
                <select value={form.kind} onChange={e=>setF('kind',e.target.value)} className="select">
                  <option value="Deployment">Deployment (stateless)</option>
                  <option value="StatefulSet">StatefulSet (stateful)</option>
                  <option value="Job">Job (run once)</option>
                  <option value="CronJob">CronJob (scheduled)</option>
                </select>
              </div>
            )}
          </div>
        )}

        {/* ── YAML mode ── */}
        {mode === 'yaml' && section('YAML Manifest',
          <>
            <div style={{ marginBottom:10 }}>
              <input ref={fileRef} type="file" accept=".yaml,.yml"
                onChange={e => {
                  const f2 = e.target.files[0];
                  if (!f2) return;
                  const reader = new FileReader();
                  reader.onload = ev => setYamlContent(ev.target.result);
                  reader.readAsText(f2);
                }}
                style={{ fontSize:12, marginBottom:8, display:'block' }} />
            </div>
            <textarea rows={18}
              placeholder={"apiVersion: apps/v1\nkind: Deployment\nmetadata:\n  name: my-service\n  namespace: i4c-apps\nspec:\n  replicas: 1\n  selector:\n    matchLabels:\n      app: my-service\n  template:\n    metadata:\n      labels:\n        app: my-service\n    spec:\n      containers:\n      - name: my-service\n        image: nginx:alpine"}
              value={yamlContent}
              onChange={e=>setYamlContent(e.target.value)}
              className="textarea" style={{ fontFamily:'monospace', minHeight:320 }} />
          </>
        )}

        {/* ── FORM mode ── */}
        {mode === 'form' && <>

          {/* Templates */}
          <div className="card">
            <div style={{ fontSize:12, fontWeight:700, color:'var(--muted)', textTransform:'uppercase',
              letterSpacing:'0.05em', marginBottom:12 }}>Quick Templates</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:7 }}>
              {SERVICE_TEMPLATES.map(tpl=>(
                <button key={tpl.name} onClick={()=>applyTemplate(tpl)}
                  className={`btn btn-sm ${form.name===tpl.name ? 'btn-primary' : 'btn-tinted-brand'}`}>
                  {tpl.label}
                </button>
              ))}
            </div>
          </div>

          {/* Service details */}
          {section('Service Details',
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div>
                {lbl('Service Name','*')}
                <input placeholder="my-backend" value={form.name}
                  onChange={e=>setF('name',e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-'))}
                  className="input" />
              </div>
              {/* Replicas has no meaning for Job/CronJob — concurrencyPolicy/parallelism
                  are the real equivalents, set in the Job/CronJob section below. */}
              {!isBatchKind(form.kind) && (
                <div>
                  {lbl('Replicas')}
                  <input type="number" min="0" max="20" value={form.replicas}
                    onChange={e=>setF('replicas',e.target.value)} className="input"
                    disabled={!!singleTpl} title={singleTpl ? 'This template runs a single node' : undefined} />
                </div>
              )}
              {singleTpl && (
                <div className="banner" style={{ gridColumn:'1/-1', fontSize:12 }}>
                  The {singleTpl.label} template runs <strong>one node</strong> — more replicas would be
                  separate copies, not a cluster.
                  {singleTpl.cluster_in_catalog && <> For a cluster use{' '}
                    <a href="/catalog" onClick={e => { e.preventDefault(); navigate('/catalog'); }}>App Catalog</a>
                    {' '}→ {singleTpl.cluster_in_catalog}.</>}
                </div>
              )}
              <div style={{ gridColumn:'1/-1' }}>
                {lbl('Container Image','*')}
                <ImageSelector value={form.image} onChange={v=>setF('image',v)}
                  placeholder="nginx:alpine, node:18, python:3.11…" />
              </div>
            </div>
          )}

          {/* Size */}
          {section('Size',
            <>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom: form.size==='custom'?16:0 }}>
                {SIZES.map(s=>(
                  <button key={s.id} onClick={()=>applySize(s.id)}
                    style={{ padding:'9px 16px', fontSize:12, fontWeight:600, borderRadius:7,
                      cursor:'pointer', border:'1.5px solid', transition:'all 0.15s',
                      background: form.size===s.id ? 'var(--brand)' : 'var(--surface)',
                      color:      form.size===s.id ? '#fff' : 'var(--ink-2)',
                      borderColor:form.size===s.id ? 'var(--brand)' : 'var(--border)' }}>
                    <div>{s.label}</div>
                    <div style={{ fontSize:10, fontWeight:400, opacity:0.8, marginTop:1 }}>{s.desc}</div>
                  </button>
                ))}
              </div>
              {form.size==='custom' && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:12 }}>
                  {[['cpu_request','vCPU Request'],['cpu_limit','vCPU Limit'],
                    ['mem_request_gb','RAM Request (GB)'],['mem_limit_gb','RAM Limit (GB)']].map(([k,l])=>(
                    <div key={k}>
                      {lbl(l)}
                      <input type="number" step="0.25" min="0.1" value={form[k]}
                        onChange={e=>setF(k,e.target.value)} className="input" />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Job / CronJob settings — restartPolicy, retry/backoff, concurrency, schedule.
              No replicas/storage/networking for these kinds (see Storage section below,
              which is hidden entirely for batch workloads — near-universally storage-less;
              a job that truly needs a volume can still be created via YAML mode). */}
          {isBatchKind(form.kind) && section(form.kind === 'CronJob' ? 'CronJob Schedule' : 'Job Settings',
            <>
              {form.kind === 'CronJob' && (
                <div style={{ marginBottom:14 }}>
                  {lbl('Cron schedule','*  e.g. */5 * * * *')}
                  <input placeholder="*/5 * * * *" value={form.schedule}
                    onChange={e=>setF('schedule',e.target.value)} className="input" />
                </div>
              )}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  {lbl('Restart Policy')}
                  <select value={form.restart_policy} onChange={e=>setF('restart_policy',e.target.value)} className="select">
                    <option value="OnFailure">OnFailure</option>
                    <option value="Never">Never</option>
                  </select>
                </div>
                {form.kind === 'CronJob' && (
                  <div>
                    {lbl('Concurrency Policy')}
                    <select value={form.concurrency_policy} onChange={e=>setF('concurrency_policy',e.target.value)} className="select">
                      <option value="Allow">Allow</option>
                      <option value="Forbid">Forbid</option>
                      <option value="Replace">Replace</option>
                    </select>
                  </div>
                )}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  {lbl('Backoff Limit','retries before failing')}
                  <input type="number" min="0" placeholder="6" value={form.backoff_limit}
                    onChange={e=>setF('backoff_limit',e.target.value)} className="input" />
                </div>
                <div>
                  {lbl('Completions')}
                  <input type="number" min="1" placeholder="1" value={form.completions}
                    onChange={e=>setF('completions',e.target.value)} className="input" />
                </div>
                <div>
                  {lbl('Parallelism')}
                  <input type="number" min="1" placeholder="1" value={form.parallelism}
                    onChange={e=>setF('parallelism',e.target.value)} className="input" />
                </div>
              </div>
              <div style={{ marginBottom: form.kind === 'CronJob' ? 14 : 0 }}>
                {lbl('Active Deadline (seconds)','optional — timeout')}
                <input type="number" min="1" placeholder="no timeout" value={form.active_deadline_seconds}
                  onChange={e=>setF('active_deadline_seconds',e.target.value)} className="input" />
              </div>
              {form.kind === 'CronJob' && (
                <>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                    <div>
                      {lbl('Successful History Limit')}
                      <input type="number" min="0" placeholder="3" value={form.successful_jobs_history_limit}
                        onChange={e=>setF('successful_jobs_history_limit',e.target.value)} className="input" />
                    </div>
                    <div>
                      {lbl('Failed History Limit')}
                      <input type="number" min="0" placeholder="1" value={form.failed_jobs_history_limit}
                        onChange={e=>setF('failed_jobs_history_limit',e.target.value)} className="input" />
                    </div>
                  </div>
                  <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, cursor:'pointer' }}>
                    <input type="checkbox" checked={form.suspend}
                      onChange={e=>setF('suspend',e.target.checked)} />
                    Suspend (create without running on schedule yet)
                  </label>
                </>
              )}
            </>
          )}

          {/* Storage — hidden for Job/CronJob (near-universally storage-less; use YAML
              mode if a job truly needs a volume). */}
          {!isBatchKind(form.kind) && section('Storage',
            <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div>
                {lbl('Storage GB','(PVC for StatefulSet, emptyDir for Deployment)')}
                <input type="number" placeholder="50" value={form.storage_gb}
                  onChange={e=>setF('storage_gb',e.target.value)} className="input" />
              </div>
              <div>
                {lbl('Storage Class')}
                <input placeholder="local-path" value={form.storage_class}
                  onChange={e=>setF('storage_class',e.target.value)} className="input" />
              </div>
            </div>
            {/* Attach an EXISTING PVC (created in Storage → PVCs) as a mounted volume */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginTop:14 }}>
              <div>
                {lbl('Attach existing PVC','(optional — mount a pre-created PVC)')}
                <input placeholder="existing-pvc-name" value={form.existing_pvc}
                  onChange={e=>setF('existing_pvc', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-'))} className="input" />
              </div>
              <div>
                {lbl('Mount path')}
                <input placeholder="/mnt/data" value={form.existing_pvc_mount}
                  onChange={e=>setF('existing_pvc_mount', e.target.value)} className="input" />
              </div>
            </div>
            </>
          , true)}

          {/* Volumes — mount an EXISTING ConfigMap/Secret/PVC as a FILE at a chosen path.
              Distinct from "ConfigMaps & Secrets" below, which is envFrom (environment
              variables), not a file mount. Hidden for Job/CronJob same as Storage above. */}
          {!isBatchKind(form.kind) && section('Volumes',
            <>
              {form.volumes.map((v, i) => {
                const srcList = v.type === 'configMap' ? configRes.configmaps.map(c=>c.name)
                              : v.type === 'secret' ? configRes.secrets.map(s=>s.name)
                              : pvcs;
                return (
                  <div key={i} style={{ display:'grid', gridTemplateColumns:'140px 1fr 1fr 32px', gap:10, alignItems:'center', marginBottom:8 }}>
                    <select value={v.type} onChange={e=>setVolume(i,'type',e.target.value)} className="select">
                      <option value="configMap">ConfigMap</option>
                      <option value="secret">Secret</option>
                      <option value="persistentVolumeClaim">PVC</option>
                    </select>
                    <select value={v.name_ref} onChange={e=>setVolume(i,'name_ref',e.target.value)} className="select">
                      <option value="" disabled>{srcList.length ? 'Select…' : '(none found in this namespace)'}</option>
                      {srcList.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <input placeholder="/etc/config" value={v.mount_path}
                      onChange={e=>setVolume(i,'mount_path',e.target.value)} className="input" />
                    <button type="button" onClick={()=>removeVolume(i)}
                      style={{ background:'none', border:'none', color:'var(--danger)', cursor:'pointer', fontSize:16 }}>✕</button>
                  </div>
                );
              })}
              <button type="button" onClick={addVolume}
                style={{ background:'none', border:'1px dashed var(--border)', borderRadius:6,
                  padding:'8px 14px', cursor:'pointer', fontSize:12, color:'var(--accent)' }}>
                + Add Volume
              </button>
            </>
          , true)}

          {/* Config — ConfigMaps & Secrets */}
          {section('ConfigMaps & Secrets',
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
              <div>
                {lbl('ConfigMaps to mount as env')}
                {configRes.configmaps.length === 0 ? (
                  <div style={{ fontSize:12, color:'var(--muted-2)', padding:'8px 0' }}>
                    No ConfigMaps found (click Refresh after selecting cluster)
                  </div>
                ) : (
                  <div style={{ border:'1px solid var(--border)', borderRadius:6, maxHeight:160, overflowY:'auto' }}>
                    {configRes.configmaps.map(cm => (
                      <label key={`${cm.namespace}/${cm.name}`} style={{ display:'flex', alignItems:'center', gap:8,
                        padding:'7px 10px', cursor:'pointer', fontSize:12,
                        borderBottom:'1px solid var(--surface-2)' }}>
                        <input type="checkbox"
                          checked={form.configmap_refs.includes(cm.name)}
                          onChange={() => toggleRef('configmap_refs', cm.name)} />
                        <span style={{ fontFamily:'monospace' }}>{cm.name}</span>
                        <span style={{ color:'var(--muted-2)', fontSize:11 }}>{cm.namespace}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                {lbl('Secrets to mount as env')}
                {configRes.secrets.length === 0 ? (
                  <div style={{ fontSize:12, color:'var(--muted-2)', padding:'8px 0' }}>
                    No Secrets found
                  </div>
                ) : (
                  <div style={{ border:'1px solid var(--border)', borderRadius:6, maxHeight:160, overflowY:'auto' }}>
                    {configRes.secrets.map(s => (
                      <label key={`${s.namespace}/${s.name}`} style={{ display:'flex', alignItems:'center', gap:8,
                        padding:'7px 10px', cursor:'pointer', fontSize:12,
                        borderBottom:'1px solid var(--surface-2)' }}>
                        <input type="checkbox"
                          checked={form.secret_refs.includes(s.name)}
                          onChange={() => toggleRef('secret_refs', s.name)} />
                        <span style={{ fontFamily:'monospace' }}>{s.name}</span>
                        <span style={{ color:'var(--muted-2)', fontSize:11 }}>{s.namespace}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          , true)}

          {/* Env vars */}
          {section('Environment Variables',
            <>
              <textarea rows={5} placeholder={'{\n  "NODE_ENV": "production",\n  "POSTGRES_PASSWORD": ""\n}'}
                value={form.env_vars} onChange={e=>setF('env_vars',e.target.value)}
                className="textarea" style={{ fontFamily:'monospace' }} />
              <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:8 }}>
                <button type="button" className="btn btn-sm btn-tinted-brand" onClick={generateBlankEnvValues}>
                  🎲 Generate random values for blank fields
                </button>
                <span style={{ fontSize:11, color:'var(--muted-2)' }}>
                  Leave a value as <code>""</code> (e.g. <code>"POSTGRES_PASSWORD": ""</code>) to have it filled
                  with a strong random value instead of typing one.
                </span>
              </div>
            </>
          , true)}

          {section('Labels',
            <div>
              {form.labels.map((l, i) => (
                <div key={i} style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
                  <input value={l.key} onChange={e=>setLabel(i,'key',e.target.value)}
                    placeholder="team" className="input" style={{ flex:1 }} />
                  <span style={{ color:'var(--muted-2)' }}>=</span>
                  <input value={l.value} onChange={e=>setLabel(i,'value',e.target.value)}
                    placeholder="platform" className="input" style={{ flex:1 }} />
                  <button type="button" onClick={()=>removeLabel(i)}
                    style={{ background:'none', border:'none', color:'var(--color-danger-500,#d9534f)', cursor:'pointer', fontSize:14 }}>✕</button>
                </div>
              ))}
              <button type="button" onClick={addLabel}
                style={{ background:'none', border:'1px dashed var(--brand)', color:'var(--brand)',
                  borderRadius:5, padding:'3px 10px', fontSize:12, cursor:'pointer' }}>
                + Add Label
              </button>
              <div style={{ marginTop:6, fontSize:11, color:'var(--muted-2)' }}>
                <code>app</code> and <code>managed-by</code> are always set by SarvKube and can't be overridden here.
              </div>
            </div>
          , true)}

          {/* Scheduling & Registry — collapsible */}
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, overflow:'hidden' }}>
            <button onClick={()=>setShowScheduling(v=>!v)}
              style={{ width:'100%', padding:'14px 20px', background:'none', border:'none', cursor:'pointer',
                display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:13, fontWeight:700, color:'var(--ink)' }}>
                Scheduling &amp; Registry
                <span style={{ fontSize:11, fontWeight:400, color:'var(--muted-2)', marginLeft:8 }}>
                  node selector · tolerations · pull policy · pull secret
                </span>
              </span>
              <span style={{ fontSize:16, color:'var(--muted-2)', transition:'transform 0.2s',
                transform: showScheduling ? 'rotate(90deg)' : 'none' }}>›</span>
            </button>

            {showScheduling && (
              <div style={{ padding:'0 20px 20px', borderTop:'1px solid var(--surface-2)', display:'flex', flexDirection:'column', gap:16 }}>

                {/* Node picker — live worker capacity; click a node to place the workload there */}
                <div>
                  {lbl('Choose a node','(live CPU / memory available — click to place)')}
                  {nodesLoading ? (
                    <div style={{ fontSize:12, color:'var(--muted-2)' }}>Loading nodes…</div>
                  ) : nodes.length === 0 ? (
                    <div style={{ fontSize:12, color:'var(--muted-2)' }}>
                      No live node data (agent-only cluster or unreachable). Use the label fields below.
                    </div>
                  ) : (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(210px,1fr))', gap:10 }}>
                      {nodes.map(n => {
                        const gib = b => (b/1024/1024/1024).toFixed(1);
                        const selected = form.node_selector_key==='kubernetes.io/hostname' && form.node_selector_val===n.hostname;
                        const pct = (u,a) => a>0 ? Math.min(100, Math.round((u/a)*100)) : 0;
                        const cpuPct = pct(n.cpu.used, n.cpu.allocatable);
                        const memPct = pct(n.memory_bytes.used, n.memory_bytes.allocatable);
                        return (
                          <div key={n.name}
                            onClick={()=>{ setF('node_selector_key','kubernetes.io/hostname'); setF('node_selector_val', n.hostname); }}
                            style={{ cursor:'pointer', border:`2px solid ${selected?'var(--brand)':'var(--surface-2)'}`,
                              borderRadius:8, padding:12, background:selected?'var(--brand-tint,#eef4ff)':'var(--surface)' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                              <strong style={{ fontSize:13 }}>{n.name}</strong>
                              <span style={{ fontSize:10, color: n.ready?'#1b7f3b':'#b7791f' }}>
                                {n.is_control_plane?'control-plane':'worker'}{n.ready?'':' · NotReady'}
                              </span>
                            </div>
                            <div style={{ marginTop:8, fontSize:11 }}>
                              <div style={{ display:'flex', justifyContent:'space-between' }}>
                                <span>CPU</span><span>{n.cpu.available} / {n.cpu.allocatable} cores free</span>
                              </div>
                              <div style={{ height:5, background:'var(--surface-2)', borderRadius:3, margin:'2px 0 6px' }}>
                                <div style={{ width:`${cpuPct}%`, height:'100%', background:cpuPct>85?'#d9534f':'#4a90d9', borderRadius:3 }} />
                              </div>
                              <div style={{ display:'flex', justifyContent:'space-between' }}>
                                <span>MEM</span><span>{gib(n.memory_bytes.available)} / {gib(n.memory_bytes.allocatable)} Gi free</span>
                              </div>
                              <div style={{ height:5, background:'var(--surface-2)', borderRadius:3, marginTop:2 }}>
                                <div style={{ width:`${memPct}%`, height:'100%', background:memPct>85?'#d9534f':'#4a90d9', borderRadius:3 }} />
                              </div>
                            </div>
                            {n.role_label && <div style={{ marginTop:6, fontSize:10, color:'var(--muted-2)' }}>role: {n.role_label}</div>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ marginTop:6, fontSize:11, color:'var(--muted-2)' }}>
                    Disk / mounted-drive free space isn't shown yet (needs the node agent) — coming soon.
                  </div>
                </div>

                {/* Node Selector */}
                <div>
                  {lbl('Node Selector','(or set a label manually)')}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                    <div>
                      <div style={{ fontSize:11, color:'var(--muted-2)', marginBottom:3 }}>Label Key</div>
                      <input placeholder="kubernetes.io/hostname" value={form.node_selector_key}
                        onChange={e=>setF('node_selector_key',e.target.value)} className="input" />
                    </div>
                    <div>
                      <div style={{ fontSize:11, color:'var(--muted-2)', marginBottom:3 }}>Label Value</div>
                      <input placeholder="w1  or  w2" value={form.node_selector_val}
                        onChange={e=>setF('node_selector_val',e.target.value)} className="input" />
                    </div>
                  </div>
                  <div style={{ marginTop:5, fontSize:11, color:'var(--muted-2)' }}>
                    Examples: key=<code>kubernetes.io/hostname</code> value=<code>w2</code> · key=<code>node-type</code> value=<code>ssd</code>
                  </div>
                </div>

                {/* Tolerations */}
                <div>
                  {lbl('Toleration','(schedule on tainted nodes e.g. GPU)')}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                    <div>
                      <div style={{ fontSize:11, color:'var(--muted-2)', marginBottom:3 }}>Taint Key</div>
                      <input placeholder="nvidia.com/gpu" value={form.toleration_key}
                        onChange={e=>setF('toleration_key',e.target.value)} className="input" />
                    </div>
                    <div>
                      <div style={{ fontSize:11, color:'var(--muted-2)', marginBottom:3 }}>Effect</div>
                      <select value={form.toleration_effect}
                        onChange={e=>setF('toleration_effect',e.target.value)} className="select">
                        <option value="NoSchedule">NoSchedule</option>
                        <option value="PreferNoSchedule">PreferNoSchedule</option>
                        <option value="NoExecute">NoExecute</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* imagePullPolicy */}
                <div>
                  {lbl('Image Pull Policy')}
                  <div style={{ display:'flex', gap:8 }}>
                    {['Always','IfNotPresent','Never'].map(p=>(
                      <button key={p} onClick={()=>setF('image_pull_policy',p)}
                        className={`btn btn-sm ${form.image_pull_policy===p ? 'btn-primary' : 'btn-secondary'}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Image Pull Secret */}
                <div>
                  {lbl('Image Pull Secret','(K8s Secret name for private registry)')}
                  <input placeholder="my-registry-secret" value={form.image_pull_secret}
                    onChange={e=>setF('image_pull_secret',e.target.value)} className="input" />
                </div>
              </div>
            )}
          </div>

        </>}

        {/* Error & Deploy */}
        {error && (
          <div className="banner danger">
            {error}
          </div>
        )}

        <div style={{ display:'flex', gap:12, paddingBottom:40 }}>
          <button onClick={handleDeploy} disabled={deploying} className="btn btn-primary btn-lg">
            {deploying ? '⟳ Deploying...' : '🚀 Deploy Workload'}
          </button>
          <button onClick={()=>navigate('/compute/workloads')} className="btn btn-secondary btn-lg">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
